import * as v from 'valibot';
import { EVENT_LISTENER_METHODS, INTERNAL_EVENTS } from '../utils';
import type {
  AudioMode,
  CameraFacing,
  Layout,
  NotificationType,
  ValueOf,
} from './common';
import type { UnionToArray } from './union-to-array';

// TODO: Add more properties later, ensure these are same across all platforms
export const ParticipantSchema = v.object({
  pid: v.string(),
  name: v.string(),
  uid: v.string(),
  // audioMuted: v.boolean(),
  // videoPaused: v.boolean(),
  avatar: v.optional(v.string()),
});

type Participant = v.InferOutput<typeof ParticipantSchema>;

type _SDKEvents = {
  [k in ValueOf<
    typeof EVENT_LISTENER_METHODS.SessionStatusListener
  >]: () => void;
} & {
  [k in ValueOf<typeof EVENT_LISTENER_METHODS.MediaEventsListener>]: () => void;
} & {
  [k in ValueOf<typeof EVENT_LISTENER_METHODS.ParticipantEventsListner>]: (
    payload: Participant,
  ) => void;
} & {
  [k in ValueOf<typeof EVENT_LISTENER_METHODS.ButtonClickListener>]: () => void;
};

export type SDKEvents = Omit<_SDKEvents, 'onParticipantListChanged'> & {
  onCallLayoutChanged: (payload: Layout) => void;
  onParticipantListVisible: () => void;
  onParticipantListHidden: () => void;
  onParticipantListChanged: (payload: Participant[]) => void;
};

export type MobileSDKEvents = SDKEvents & {
  onAudioModeChanged: (payload: AudioMode['type']) => void; // TODO: TEST
  onCameraFacingChanged: (payload: CameraFacing) => void; // TODO: TEST iOS
  onSwitchCameraButtonClicked: () => void;
  onPictureInPictureLayoutEnabled: () => void;
  onPictureInPictureLayoutDisabled: () => void;
};

export type WebSDKEvents = SDKEvents & {
  onAudioInputDeviceChanged: (device: MediaDeviceInfo) => void;
  onVideoInputDeviceChanged: (device: MediaDeviceInfo) => void;
  onAudioOutputDeviceChanged: (device: MediaDeviceInfo) => void;
  onAudioInputDevicesChanged: (device: MediaDeviceInfo[]) => void;
  onVideoInputDevicesChanged: (device: MediaDeviceInfo[]) => void;
  onAudioOutputDevicesChanged: (device: MediaDeviceInfo[]) => void;
};

export type InternalEvents = {
  [INTERNAL_EVENTS.onConnectionEstablished]: () => void;
  [INTERNAL_EVENTS.connectionWillClose]: () => void;
  [INTERNAL_EVENTS.notification]: (payload: {
    message: string;
    type: NotificationType;
    duration?: number;
    position?: 'top' | 'bottom';
    action?: { label: string; onPress: () => void };
  }) => void;
  [INTERNAL_EVENTS.lifecycle.componentDidMount]: () => void;
  [INTERNAL_EVENTS.lifecycle.componentWillUnmount]: () => void;
  [INTERNAL_EVENTS.onSessionJoined]: () => void;
  [INTERNAL_EVENTS.lifecycle.SDKInitialized]: () => void;
  [INTERNAL_EVENTS.queryPermission]: () => void;
};

type AllSDKEvents = MobileSDKEvents & WebSDKEvents;

export type AllEvents = AllSDKEvents & InternalEvents;

type _NativeListenerActions = {
  [K in keyof AllSDKEvents]: {
    type: K;
  } & (Parameters<AllSDKEvents[K]>[0] extends undefined
    ? {}
    : { payload: Parameters<AllSDKEvents[K]>[0] });
};

export type NativeListenerActions = UnionToArray<
  _NativeListenerActions[keyof _NativeListenerActions]
>;
